import StaticPageLayout from "@/components/StaticPageLayout";

function Section({ heading, children, testId }) {
  return (
    <section className="mt-10" data-testid={testId}>
      <h2 className="font-display text-xl md:text-2xl tracking-tight">
        {heading}
      </h2>
      <div className="mt-3 text-white/75 leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <StaticPageLayout
      eyebrow="Legal"
      title="Privacy Policy"
      description="How Giprompthub collects, uses, and protects information about visitors to our site."
      metaTitle="Privacy Policy — Giprompthub"
      metaDescription="Read the Giprompthub privacy policy: log files, analytics, cookies, DART, GDPR, CCPA, COPPA, and third-party advertising practices."
    >
      <p className="text-white/75 leading-relaxed" data-testid="privacy-intro">
        At <strong className="text-white">Giprompthub.com</strong>, accessible
        from{" "}
        <a
          href="https://giprompthub.com"
          className="text-[#00E5FF] hover:text-white underline underline-offset-4"
        >
          https://giprompthub.com
        </a>
        , the privacy of our visitors is one of our main priorities. This
        Privacy Policy document outlines the types of information that is
        collected and recorded by Giprompthub.com and how we use it. If you
        have additional questions or require more information about this
        Privacy Policy, please contact us through our{" "}
        <a
          href="/contact"
          className="text-[#00E5FF] hover:text-white underline underline-offset-4"
        >
          Contact page
        </a>
        . This Privacy Policy applies only to our online activities and is
        valid for visitors to our website with regard to the information that
        they shared and/or collect on Giprompthub.com. This policy is not
        applicable to any information collected offline or via channels other
        than this website.
      </p>

      <Section heading="Consent" testId="privacy-consent">
        <p>
          By using our website, you hereby consent to our Privacy Policy and
          agree to its terms. Continued use of the site after any changes to
          this policy constitutes your acceptance of those changes.
        </p>
      </Section>

      <Section heading="Log Files" testId="privacy-log-files">
        <p>
          Giprompthub.com follows a standard procedure of using log files.
          These files log visitors when they visit websites. All hosting
          companies do this as part of hosting services' analytics. The
          information collected by log files includes internet protocol (IP)
          addresses, browser type, Internet Service Provider (ISP), date and
          time stamp, referring/exit pages, and possibly the number of
          clicks. These are not linked to any information that is personally
          identifiable. The purpose of this information is for analyzing
          trends, administering the site, tracking users' movement on the
          website, and gathering demographic information.
        </p>
        <p>
          We also use Google Analytics to understand aggregate visitor
          behavior — such as which prompt categories are most popular and how
          users navigate between pages — so we can continue improving the
          site experience.
        </p>
      </Section>

      <Section heading="Cookies and Web Beacons" testId="privacy-cookies">
        <p>
          Like any other website, Giprompthub.com uses &lsquo;cookies&rsquo;
          to store information including visitors&rsquo; preferences and the
          pages on the website that the visitor accessed or visited. This
          information is used to optimize users' experience by customizing
          our web page content based on visitors' browser type and/or other
          information.
        </p>
      </Section>

      <Section
        heading="Google DART Cookies for Google AdSense"
        testId="privacy-dart-cookies"
      >
        <p>
          Google, as a third-party vendor, uses cookies to serve ads on
          Giprompthub.com. Google's use of the DART cookie enables it to
          serve ads to our visitors based on their visit to our site and
          other sites on the internet. Visitors may opt out of the use of the
          DART cookie by visiting the{" "}
          <a
            href="https://policies.google.com/technologies/ads"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#00E5FF] hover:text-white underline underline-offset-4"
          >
            Google ad and content network Privacy Policy
          </a>
          . We may run Google AdSense advertisements on this website. Google,
          as a third-party vendor, uses cookies to serve ads. Google's use of
          advertising cookies enables it and its partners to serve ads based
          on your visit to this site and/or other sites on the internet. You
          may opt out of personalized advertising by visiting{" "}
          <a
            href="https://adssettings.google.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#00E5FF] hover:text-white underline underline-offset-4"
          >
            Google Ads Settings
          </a>
          .
        </p>
      </Section>

      <Section heading="Advertising Partners Privacy Policies" testId="privacy-ad-partners">
        <p>
          You may consult this list to find the Privacy Policy for each of
          the advertising partners of Giprompthub.com. Third-party ad servers
          or ad networks use technologies like cookies, JavaScript, or Web
          Beacons that are used in their respective advertisements and links
          that appear on Giprompthub.com, which are sent directly to users'
          browsers. They automatically receive your IP address when this
          occurs. These technologies are used to measure the effectiveness of
          their advertising campaigns and/or to personalize the advertising
          content that you see on websites that you visit.
        </p>
        <p>
          Note that Giprompthub.com has no access to or control over these
          cookies that are used by third-party advertisers.
        </p>
      </Section>

      <Section heading="Third Party Privacy Policies" testId="privacy-third-party">
        <p>
          Giprompthub.com&rsquo;s Privacy Policy does not apply to other
          advertisers or websites. We advise you to consult the respective
          Privacy Policies of these third-party ad servers for more detailed
          information. It may include their practices and instructions about
          how to opt out of certain options. You may find a complete list of
          these Privacy Policies and their links here: the Google
          Advertising Privacy &amp; Terms page.
        </p>
        <p>
          You can choose to disable cookies through your individual browser
          options. To know more detailed information about cookie management
          with specific web browsers, it can be found at the browsers'
          respective websites.
        </p>
      </Section>

      <Section heading="GDPR Data Protection Rights" testId="privacy-gdpr">
        <p>
          If you are located in the European Economic Area (EEA), you are
          entitled to certain data protection rights under the General Data
          Protection Regulation (GDPR). We want all our visitors to be fully
          aware of their data protection rights. Every visitor is entitled to
          the following:
        </p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>
            <strong className="text-white">The right to access</strong> — You
            have the right to request copies of your personal data.
          </li>
          <li>
            <strong className="text-white">The right to rectification</strong>{" "}
            — You have the right to request that we correct any information
            you believe is inaccurate or complete information you believe is
            incomplete.
          </li>
          <li>
            <strong className="text-white">The right to erasure</strong> —
            You have the right to request that we erase your personal data,
            under certain conditions.
          </li>
          <li>
            <strong className="text-white">
              The right to restrict processing
            </strong>{" "}
            — You have the right to request that we restrict the processing
            of your personal data, under certain conditions.
          </li>
          <li>
            <strong className="text-white">
              The right to object to processing
            </strong>{" "}
            — You have the right to object to our processing of your personal
            data, under certain conditions.
          </li>
          <li>
            <strong className="text-white">
              The right to data portability
            </strong>{" "}
            — You have the right to request that we transfer the data we
            have collected to another organization, or directly to you,
            under certain conditions.
          </li>
        </ul>
        <p>
          If you make a request, we have one month to respond to you. If you
          would like to exercise any of these rights, please contact us
          through our Contact page.
        </p>
      </Section>

      <Section heading="CCPA Privacy Rights" testId="privacy-ccpa">
        <p>
          Under the California Consumer Privacy Act (CCPA), among other
          rights, California consumers have the right to: request that a
          business that collects a consumer's personal data disclose the
          categories and specific pieces of personal data that it has
          collected; request that a business delete any personal data about
          the consumer that it has collected; and request that a business
          that sells a consumer's personal data not sell the consumer's
          personal data. If you make a request, we have one month to respond
          to you. If you would like to exercise any of these rights, please
          contact us.
        </p>
      </Section>

      <Section heading="Children's Information (COPPA)" testId="privacy-coppa">
        <p>
          Another part of our priority is adding protection for children
          while using the internet. We encourage parents and guardians to
          observe, participate in, and/or monitor and guide their online
          activity. Giprompthub.com does not knowingly collect any Personal
          Identifiable Information from children under the age of 13. If you
          think that your child provided this kind of information on our
          website, we strongly encourage you to contact us immediately and we
          will do our best efforts to promptly remove such information from
          our records, in compliance with the Children's Online Privacy
          Protection Act (COPPA).
        </p>
      </Section>

      <Section heading="Changes to This Privacy Policy" testId="privacy-changes">
        <p>
          We may update our Privacy Policy from time to time. We will notify
          you of any changes by posting the new Privacy Policy on this page.
          You are advised to review this Privacy Policy periodically for any
          changes.
        </p>
      </Section>
    </StaticPageLayout>
  );
}
